import { redirectToBattleNetLogin } from "../utils/auth";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8080";

const handleFetch = async (url, options, token) => {
  const response = await fetch(url, {
    ...options,
    headers: { ...options.headers, Authorization: `Bearer ${token}` },
  });

  let responseBody;

  try {
    // Attempt to parse the response as JSON
    responseBody = await response.json();
  } catch (error) {
    // Handle cases where response is empty or not valid JSON
    if (response.status !== 204) {
      // Ignore empty bodies for 204 status (No Content)
      console.error("Failed to parse JSON:", error);
    }
    responseBody = {}; // Default to empty object if no body
  }

  if (response.status === 401) {
    // Handle specific conditions where the token should be deleted
    if (
      responseBody?.message === "Token expired" ||
      responseBody?.error === "Invalid token"
    ) {
      document.cookie = "jwt=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
      redirectToBattleNetLogin();
      const error = new Error("Unauthorized: Token has expired or is invalid.");
      error.status = 401;
      throw error;
    } else {
      const error = new Error("Unauthorized: Insufficient permissions.");
      error.status = 401;
      throw error;
    }
  }

  if (!response.ok) {
    const errorMessage = responseBody?.message || "An error occurred";
    const error = new Error(errorMessage);
    error.status = response.status;
    throw error;
  }

  return responseBody || {};
};

export const fetchRaidSignups = async (token) => {
  return handleFetch(
    `${API_URL}/raid-signups`,
    {
      method: "GET",
    },
    token
  );
};

export const addRaidSignup = async (
  token,
  { team, name, characterName, realm, className, spec, flexTank, flexHealer, flexDps }
) => {
  return handleFetch(
    `${API_URL}/raid-signups`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        team,
        name,
        characterName,
        realm,
        className,
        spec,
        flexTank,
        flexHealer,
        flexDps,
      }),
    },
    token
  );
};

export const updateRaidSignup = async (
  token,
  id,
  { team, name, characterName, realm, className, spec, flexTank, flexHealer, flexDps }
) => {
  return handleFetch(
    `${API_URL}/raid-signups/${id}`,
    {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        team,
        name,
        characterName,
        realm,
        className,
        spec,
        flexTank,
        flexHealer,
        flexDps,
      }),
    },
    token
  );
};

export const fetchUserCharacters = async (token) => {
  return handleFetch(
    `${API_URL}/users/characters`,
    {
      method: "GET",
    },
    token
  );
};

export const searchUsers = async (token, battleTag) => {
  return handleFetch(
    `${API_URL}/users/search?battleTag=${encodeURIComponent(battleTag)}`,
    {
      method: "GET",
    },
    token
  );
};

export const createTournament = async (token, tournament) => {
  return handleFetch(
    `${API_URL}/tournament`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(tournament),
    },
    token
  );
};

export const fetchAllTournaments = async (token) => {
  return handleFetch(
    `${API_URL}/tournament`,
    {
      method: "GET",
    },
    token
  );
};

export const fetchTournamentById = async (token, id) => {
  return handleFetch(
    `${API_URL}/tournaments/${id}`,
    {
      method: "GET",
    },
    token
  );
};

export const signupForTournament = async (
  token,
  id,
  characterId,
  characterName,
  characterRealm
) => {
  return handleFetch(
    `${API_URL}/tournament/${id}/signup`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        characterId,
        characterName,
        characterRealm,
      }),
    },
    token
  );
};

export async function cancelTournamentSignup(token, tournamentId) {
  return handleFetch(
    `${API_URL}/tournament/${tournamentId}/signup/cancel`,
    {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    },
    token
  );
}

export const fetchTournamentStandings = async (token, id) => {
  return handleFetch(
    `${API_URL}/tournament/${id}/standings`,
    {
      method: "GET",
    },
    token
  );
};

export const selectUserCharacter = async (token, characterData) => {
  return handleFetch(
    `${API_URL}/users/character/select`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(characterData),
    },
    token
  );
};

export const fetchUserProfile = async (token) => {
  return handleFetch(
    `${API_URL}/users/me`,
    {
      method: "GET",
    },
    token
  );
};

export const fetchRegisteredTournament = async (token, selectedCharacterId) => {
  return handleFetch(
    `${API_URL}/tournament/character/${selectedCharacterId}/standings`,
    {
      method: "GET",
    },
    token
  );
};

export const deleteTournament = async (token, id) => {
  return handleFetch(
    `${API_URL}/tournament/${id}`,
    {
      method: "DELETE",
    },
    token
  );
};

export const fetchTournamentHistory = async (token) => {
  return handleFetch(
    `${API_URL}/tournament/history`,
    {
      method: "GET",
    },
    token
  );
};

export const fetchTournamentHistoryById = async (token, id) => {
  return handleFetch(
    `${API_URL}/tournament/${id}/history`,
    {
      method: "GET",
    },
    token
  );
};

export const fetchTournamentStandingsById = async (token, id) => {
  return handleFetch(
    `${API_URL}/tournament/${id}/standings`,
    {
      method: "GET",
    },
    token
  );
};

export const fetchCharacterStandings = async (token, characterId) => {
  return handleFetch(
    `${API_URL}/tournament/character/${characterId}/standings`,
    {
      method: "GET",
    },
    token
  );
};

export const updateTournamentStandings = async (token, id) => {
  return handleFetch(
    `${API_URL}/tournament/${id}/update`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
    },
    token
  );
};

export const registerSoloDuoLadder = async (
  token,
  { type, characterId, partnerCharacterId }
) => {
  return handleFetch(
    `${API_URL}/soloduoque/register`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        type,
        characterId,
        partnerCharacterId,
      }),
    },
    token
  );
};

export const cancelSoloDuoLadderRegistration = async (token) => {
  return handleFetch(
    `${API_URL}/soloduoque/register`,
    {
      method: "DELETE",
    },
    token
  );
};

export const cancelSoloDuoRegistrationById = async (token, registrationId) => {
  return handleFetch(
    `${API_URL}/soloduoque/register/${registrationId}`,
    {
      method: "DELETE",
    },
    token
  );
};

export const sendSoloDuoInvitation = async (token, { toUserId, myCharacterId }) => {
  return handleFetch(
    `${API_URL}/soloduoque/invitations`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        toUserId,
        myCharacterId,
      }),
    },
    token
  );
};

export const fetchSoloDuoInvitations = async (token) => {
  return handleFetch(
    `${API_URL}/soloduoque/invitations`,
    {
      method: "GET",
    },
    token
  );
};

export const fetchSentSoloDuoInvitations = async (token) => {
  return handleFetch(
    `${API_URL}/soloduoque/invitations/sent`,
    {
      method: "GET",
    },
    token
  );
};

export const fetchSoloDuoInvitationById = async (token, id) => {
  return handleFetch(
    `${API_URL}/soloduoque/invitations/${id}`,
    {
      method: "GET",
    },
    token
  );
};

export const cancelSoloDuoInvitation = async (token, id) => {
  return handleFetch(
    `${API_URL}/soloduoque/invitations/${id}`,
    {
      method: "DELETE",
    },
    token
  );
};

export const acceptSoloDuoInvitation = async (token, id, myCharacterId) => {
  const body = myCharacterId ? { myCharacterId } : {}
  return handleFetch(
    `${API_URL}/soloduoque/invitations/${id}/accept`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    },
    token
  );
};

export const declineSoloDuoInvitation = async (token, id) => {
  return handleFetch(
    `${API_URL}/soloduoque/invitations/${id}/decline`,
    {
      method: "POST",
    },
    token
  );
};

export const fetchMySoloDuoRegistration = async (token) => {
  return handleFetch(
    `${API_URL}/soloduoque/my-registration`,
    {
      method: "GET",
    },
    token
  );
};

export const syncSoloDuoRuns = async (token) => {
  return handleFetch(
    `${API_URL}/soloduoque/sync`,
    {
      method: "POST",
    },
    token
  );
};

export const fetchMySoloDuoRuns = async (token) => {
  return handleFetch(
    `${API_URL}/soloduoque/my-runs`,
    {
      method: "GET",
    },
    token
  );
};

export const fetchSoloDuoRules = async (token) => {
  return handleFetch(
    `${API_URL}/soloduoque/rules`,
    {
      method: "GET",
    },
    token
  );
};

export const fetchSoloDuoStandings = async (
  token,
  { limit, offset, type } = {}
) => {
  const params = new URLSearchParams();

  if (typeof limit === "number") {
    params.append("limit", limit);
  }

  if (typeof offset === "number") {
    params.append("offset", offset);
  }

  if (type) {
    params.append("type", type);
  }

  const queryString = params.toString();
  const url = queryString
    ? `${API_URL}/soloduoque/standings?${queryString}`
    : `${API_URL}/soloduoque/standings`;

  return handleFetch(
    url,
    {
      method: "GET",
    },
    token
  );
};

export const fetchSoloDuoStandingsHistory = async (token, { limit } = {}) => {
  const params = new URLSearchParams();

  if (typeof limit === "number") {
    params.append("limit", limit);
  }

  const queryString = params.toString();
  const url = queryString
    ? `${API_URL}/soloduoque/standings/history?${queryString}`
    : `${API_URL}/soloduoque/standings/history`;

  return handleFetch(
    url,
    {
      method: "GET",
    },
    token
  );
};

export const fetchSoloDuoRegistrationRuns = async (token, registrationId) => {
  return handleFetch(
    `${API_URL}/soloduoque/registrations/${registrationId}/runs`,
    {
      method: "GET",
    },
    token
  );
};

/**
 * Admin-only: delete any solo/duo registration by id.
 * Callable for every registration; backend must allow users with admin role without ownership checks.
 */
export const adminDeleteSoloDuoRegistration = async (token, id) => {
  return handleFetch(
    `${API_URL}/soloduoque/admin/registrations/${id}`,
    {
      method: "DELETE",
    },
    token
  );
};

/**
 * Admin-only: fetch the audit log for the raid signups page.
 * Backend must restrict this endpoint to users with the admin role.
 */
export const fetchRaidSignupAuditLog = async (token) => {
  return handleFetch(
    `${API_URL}/raid-signups/admin/audit-log`,
    {
      method: "GET",
    },
    token
  );
};

/**
 * Admin-only: remove a raid sign-up by its id (moderation - spam, duplicates, joke entries).
 * Logged to the audit trail by the backend.
 */
export const adminDeleteRaidSignup = async (token, id) => {
  return handleFetch(
    `${API_URL}/raid-signups/admin/${id}`,
    {
      method: "DELETE",
    },
    token
  );
};

export const fetchCurrentRaidTeam = async (token) => {
  return handleFetch(
    `${API_URL}/raid-teams/current`,
    {
      method: "GET",
    },
    token
  );
};

export const fetchRaidReports = async (token, { limit, offset } = {}) => {
  const params = new URLSearchParams();

  if (typeof limit === "number") {
    params.append("limit", limit);
  }

  if (typeof offset === "number") {
    params.append("offset", offset);
  }

  const queryString = params.toString();
  const url = queryString
    ? `${API_URL}/raid-stats/reports?${queryString}`
    : `${API_URL}/raid-stats/reports`;

  return handleFetch(
    url,
    {
      method: "GET",
    },
    token
  );
};

export const fetchRaidReportByCode = async (token, code) => {
  return handleFetch(
    `${API_URL}/raid-stats/reports/${code}`,
    {
      method: "GET",
    },
    token
  );
};

/**
 * Resolved live from WCL per-request (one WCL call) rather than DB-only, so
 * only call this lazily for a single opened fight, never for a whole report up front.
 * spec/role/rankPercent are always null in the response since WCL's table() only exposes class.
 */
export const fetchRaidFightParticipants = async (token, code, wclFightId) => {
  return handleFetch(
    `${API_URL}/raid-stats/reports/${code}/fights/${wclFightId}/participants`,
    {
      method: "GET",
    },
    token
  );
};

/**
 * Resolved live from WCL per-request, same caveat as fetchRaidFightParticipants:
 * call lazily for one opened fight at a time, never in bulk.
 */
export const fetchRaidFightDeaths = async (token, code, wclFightId) => {
  return handleFetch(
    `${API_URL}/raid-stats/reports/${code}/fights/${wclFightId}/deaths`,
    {
      method: "GET",
    },
    token
  );
};

/**
 * Resolved live from WCL per-request. `ability` is matched server-side as a
 * case-insensitive substring (e.g. "Healthstone" also matches "Demonic Healthstone").
 */
export const fetchRaidFightCasts = async (token, code, wclFightId, ability) => {
  const params = new URLSearchParams();
  params.append("ability", ability);
  return handleFetch(
    `${API_URL}/raid-stats/reports/${code}/fights/${wclFightId}/casts?${params.toString()}`,
    {
      method: "GET",
    },
    token
  );
};

export const fetchCharacterRaidStats = async (token, characterId) => {
  return handleFetch(
    `${API_URL}/raid-stats/characters/${characterId}`,
    {
      method: "GET",
    },
    token
  );
};

export const searchSoloDuoCharacters = async (token, q, limit = 20) => {
  const params = new URLSearchParams();
  params.append("q", q);
  if (typeof limit === "number") {
    params.append("limit", limit);
  }
  const url = `${API_URL}/soloduoque/search-characters?${params.toString()}`;
  return handleFetch(
    url,
    {
      method: "GET",
    },
    token
  );
};